import { defineStore } from 'pinia'
import * as sdk from '@/helpers/sdk'

import useRouterStore from '@/stores/router.store'
import useProfileStore, { profileKey } from '@/stores/profile.store'
import profileQueue from '@/helpers/profileQueue'
import getRedirectAfterDelete from '@/helpers/profile/getRedirectAfterDelete'
import routeNames from '@/router/names'
import useAction from '@/composables/useAction'

const meta = {
  requestPermission: {
    read: true,
    edit: true,
  },
}

async function createGlobalChannel(payload = {}) {
  const profileStore = useProfileStore()
  const routerStore = useRouterStore()

  const profile = profileStore[profileKey]

  const {
    globalChannels,
  } = profileStore // current

  let newOrder = 0
  const order = Array.isArray(payload.channels) ? payload.channels[0]?._order : payload._order
  if (typeof order === 'number') {
    newOrder = order
  } else if (globalChannels) {
    newOrder = globalChannels.length
  }

  const result = await profileQueue.add(
    () => sdk.v3.profiles.global.createChannel({
      url: {
        profileUid: profile.uid,
      },
      body: payload,
    }),
  ).result

  profileStore[profileKey] = result.data

  // Redirect based on index, to select the newly created preset
  await routerStore.push({
    name: routeNames.GlobalChannel,
    params: {
      profileUid: profileStore.profileUid,
      channelUid: newOrder,
    },
  })

  return result.data
}

async function createLocalChannel(payload = {}) {
  const profileStore = useProfileStore()
  const routerStore = useRouterStore()

  const profile = profileStore[profileKey]

  const {
    mode,
    localChannels,
  } = profileStore // current

  let newOrder = 0
  const order = Array.isArray(payload.channels) ? payload.channels[0]?._order : payload._order
  if (typeof order === 'number') {
    newOrder = order
  } else if (localChannels) {
    newOrder = localChannels.length
  }

  const result = await profileQueue.add(
    () => sdk.v3.profiles.modes.createChannel({
      url: {
        profileUid: profile.uid,
        mUid: mode.data._uid,
      },
      body: payload,
    }),
  ).result

  profileStore[profileKey] = result.data

  // Redirect based on index, to select the newly created preset
  await routerStore.push({
    name: routeNames.LocalChannel,
    params: {
      profileUid: profileStore.profileUid,
      modeUid: profileStore.modeUid,
      channelUid: newOrder,
    },
  })

  return result.data
}

async function duplicateGlobalChannel({ _uid } = {}) {
  const profileStore = useProfileStore()
  const routerStore = useRouterStore()

  const profile = profileStore[profileKey]

  const {
    channel,
  } = profileStore // current

  const newOrder = channel.data._order + 1

  const result = await profileQueue.add(
    () => sdk.v3.profiles.global.copyChannel({
      url: {
        profileUid: profile.uid,
        cUid: (
          _uid || channel.data._uid
        ),
      },
    }),
  ).result

  profileStore[profileKey] = result.data

  // Redirect based on index, to select the copied preset
  // TODO: redirect to the presetRange/preset if any selected ?
  await routerStore.push({
    name: routeNames.GlobalChannel,
    params: {
      profileUid: profileStore.profileUid,
      channelUid: newOrder,
    },
  })

  return result.data
}

async function duplicateLocalChannel({ _uid } = {}) {
  const profileStore = useProfileStore()
  const routerStore = useRouterStore()

  const profile = profileStore[profileKey]

  const {
    mode,
    channel,
  } = profileStore // current

  const newOrder = channel.data._order + 1

  const result = await profileQueue.add(
    () => sdk.v3.profiles.modes.copyChannel({
      url: {
        profileUid: profile.uid,
        mUid: mode.data._uid,
        cUid: (
          _uid || channel.data._uid
        ),
      },
    }),
  ).result

  profileStore[profileKey] = result.data

  // Redirect based on index, to select the newly created preset
  // TODO: redirect to the presetRange/preset if any selected ?
  await routerStore.push({
    name: routeNames.LocalChannel,
    params: {
      profileUid: profileStore.profileUid,
      modeUid: profileStore.modeUid,
      channelUid: newOrder,
    },
  })

  return result.data
}

async function editGlobalChannel({ _uid, ...payload }) {
  const profileStore = useProfileStore()

  const profile = profileStore[profileKey]
  const { globalChannel } = profileStore // current

  const result = await profileQueue.add(
    () => sdk.v3.profiles.global.editChannel({
      url: {
        profileUid: profile.uid,
        cUid: (
          _uid || globalChannel.data._uid
        ),
      },
      body: payload,
    }),
  ).result

  profileStore[profileKey] = result.data
  return result.data
}

async function editLocalChannel({ _uid, ...payload } = {}) {
  const profileStore = useProfileStore()

  const profile = profileStore[profileKey]
  const { mode, localChannel } = profileStore // current

  const result = await profileQueue.add(
    () => sdk.v3.profiles.modes.editChannel({
      url: {
        profileUid: profile.uid,
        mUid: mode.data._uid,
        cUid: (
          _uid || localChannel.data._uid
        ),
      },
      body: payload,
    }),
  ).result

  profileStore[profileKey] = result.data
  return result.data
}

async function deleteGlobalChannel({ _uid } = {}) {
  const profileStore = useProfileStore()
  const routerStore = useRouterStore()

  const profile = profileStore[profileKey]

  const {
    globalChannel,
  } = profileStore // current

  const redirectTo = getRedirectAfterDelete(globalChannel)

  const result = await profileQueue.add(
    () => sdk.v3.profiles.global.deleteChannel({
      url: {
        profileUid: profile.uid,
        cUid: (
          _uid || globalChannel.data._uid
        ),
      },
    }),
  ).result

  profileStore[profileKey] = result.data

  // Redirect to the closest channel
  await routerStore.push(redirectTo)

  return result.data
}

async function deleteLocalChannel({ _uid } = {}) {
  const profileStore = useProfileStore()
  const routerStore = useRouterStore()

  const profile = profileStore[profileKey]

  const {
    mode,
    localChannel,
  } = profileStore // current

  const redirectTo = getRedirectAfterDelete(localChannel)

  const result = await profileQueue.add(
    () => sdk.v3.profiles.modes.deleteChannel({
      url: {
        profileUid: profile.uid,
        mUid: mode.data._uid,
        cUid: (
          _uid || localChannel.data._uid
        ),
      },
    }),
  ).result

  profileStore[profileKey] = result.data

  // Redirect to the closest channel
  await routerStore.push(redirectTo)

  return result.data
}

async function resetToGlobal({ _uid } = {}) {
  const profileStore = useProfileStore()
  const routerStore = useRouterStore()

  const profile = profileStore[profileKey]

  const {
    mode,
    localChannel,
  } = profileStore // current

  const result = await profileQueue.add(
    () => sdk.v3.profiles.modes.resetChannelToGlobal({
      url: {
        profileUid: profile.uid,
        mUid: mode.data._uid,
        cUid: (
          _uid || localChannel.data._uid
        ),
      },
    }),
  ).result

  profileStore[profileKey] = result.data

  // Redirect to the channel that was reset to global
  await routerStore.push({
    name: routeNames.LocalChannel,
    params: {
      profileUid: profileStore.profileUid,
      modeUid: profileStore.modeUid,
      channelUid: _uid || localChannel.data._uid,
    },
  })

  return result.data
}

const useChannelStore = defineStore('profile/channel', () => {
  const { action: createGlobal, status } = useAction(createGlobalChannel, { meta })

  return {
    status,
    createGlobal,
    createLocal: useAction(createLocalChannel, { status, meta }).action,
    duplicateGlobal: useAction(duplicateGlobalChannel, { status, meta }).action,
    duplicateLocal: useAction(duplicateLocalChannel, { status, meta }).action,
    editGlobal: useAction(editGlobalChannel, { status, meta }).action,
    editLocal: useAction(editLocalChannel, { status, meta }).action,
    resetToGlobal: useAction(resetToGlobal, { status, meta }).action,
    deleteGlobal: useAction(deleteGlobalChannel, { status, meta }).action,
    deleteLocal: useAction(deleteLocalChannel, { status, meta }).action,
  }
})

export default useChannelStore
